import axios from "axios";
import React, { useState, useEffect, useRef } from "react";
import debounce from "lodash.debounce";

const Step3PersonalDetails = ({
  formData,
  setFormData,
  onBack,
  onThankYou,
}) => {
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [states, setStates] = useState([]);
  const [suburbOptions, setSuburbOptions] = useState([]);
  const [suburbInput, setSuburbInput] = useState("");
  const [suburbLoading, setSuburbLoading] = useState(false);
  const [isSuburbSelected, setIsSuburbSelected] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;

  // --- UI Classes ---
  const labelClass = "block text-gray-800 font-semibold mb-1";
  const inputClass =
    "w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-colors duration-200";
  const errorClass = "text-red-600 text-xs mt-1 font-medium";
  const subheadingClass =
    "text-xl font-bold text-blue-700 mb-3 border-b-2 border-blue-300 pb-1";
  const buttonBarClass = "flex justify-between mt-8";
  const backButtonClass =
    "bg-gray-200 text-gray-800 px-6 py-2 rounded-lg font-semibold hover:bg-gray-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-300";
  const submitButtonClass =
    "bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 shadow-md transition focus:outline-none focus:ring-4 focus:ring-green-300 disabled:opacity-50 disabled:cursor-not-allowed";

  // List of required fields (those with * in label)
  const requiredFields = [
    "title",
    "gender",
    "family_name",
    "first_name",
    "dob",
    "address",
    "state",
    "suburb",
    "postcode",
    "mobile_number",
    "transport",
    "referral_source",
    "location",
    "experience",
  ];

  // Validation function
  const validate = () => {
    const newErrors = {};

    requiredFields.forEach((field) => {
      if (!formData[field] || formData[field].toString().trim() === "") {
        newErrors[field] = "This field is required";
      }
    });

    // Mobile number validation (basic)
    if (
      formData.mobile_number &&
      !/^\+?\d{7,15}$/.test(formData.mobile_number)
    ) {
      newErrors.mobile_number = "Please enter a valid mobile number";
    }

    // DOB validation (must be in the past)
    if (formData.dob && new Date(formData.dob) > new Date()) {
      newErrors.dob = "Date of birth cannot be in the future";
    }

    // Postcode validation (must be 4 digits)
    if (formData.postcode && !/^\d{4}$/.test(formData.postcode)) {
      newErrors.postcode = "Please enter a valid 4-digit postcode";
    }

    // Transport validation (must be 'Car' or 'Public_Transport')
    if (
      formData.transport &&
      !["Car", "Public_Transport"].includes(formData.transport)
    ) {
      newErrors.transport = "Please select a valid transport option";
    }

    if (!isSuburbSelected) {
      newErrors.suburb = "Please select a suburb from the list";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate on every change
  useEffect(() => {
    validate();
    // eslint-disable-next-line
  }, [formData]);

  useEffect(() => {
    axios
      .get(`${apiUrl}/states`)
      .then((response) => {
        setStates(response.data);
      })
      .catch((error) => {
        console.error("Error fetching state list:", error);
      });
  }, []);

  // Debounced suburb fetcher
  const fetchSuburbs = useRef(
    debounce(async (stateCode, query) => {
      if (!stateCode) {
        setSuburbOptions([]);
        setSuburbLoading(false);
        return;
      }
      setSuburbLoading(true);
      try {
        const response = await axios.get(`${apiUrl}/locations-by-state`, {
          params: { state_code: stateCode, query },
        });
        setSuburbOptions(response.data);
      } catch (error) {
        setSuburbOptions([]);
      }
      setSuburbLoading(false);
    }, 350)
  ).current;

  // When state changes, clear suburb fields
  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    setFormData({
      ...formData,
      state: selectedState,
      suburb: "",
      postcode: "",
    });
    setSuburbInput("");
    setSuburbOptions([]);
    setIsSuburbSelected(false);

    if (selectedState) {
      fetchSuburbs(selectedState, "");
    }
  };

  // Suburb input change handler
  const handleSuburbInputChange = (e) => {
    const value = e.target.value;
    setSuburbInput(value);
    setFormData({ ...formData, suburb: value, postcode: "" });
    setIsSuburbSelected(false);
    if (formData.state) {
      fetchSuburbs(formData.state, value);
    } else {
      setSuburbOptions([]);
    }
  };

  // When user picks a suburb from suggestions
  const handleSuburbSelect = (suburb, postal_code) => {
    setFormData({ ...formData, suburb, postcode: postal_code });
    setSuburbInput(suburb);
    setSuburbOptions([]);
    setIsSuburbSelected(true);
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = new FormData();
      for (const key in formData) {
        payload.append(key, formData[key]);
      }

      await axios.post(`${apiUrl}/submit-job-application`, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (onThankYou) onThankYou();
    } catch (error) {
      console.error("Submission failed:", error);
      alert("Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 via-white to-green-50 rounded-2xl shadow-2xl border border-blue-100 p-8 max-w-3xl mx-auto space-y-6">
      <h3 className={subheadingClass}>Please complete your application</h3>

      {/* Title */}
      <div>
        <label className={labelClass}>
          Title <span className="text-red-600">*</span>
        </label>
        <select
          className={inputClass + (errors.title ? " border-red-500" : "")}
          value={formData?.title || ""}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        >
          <option value="">Select Title</option>
          <option value="Miss">MISS</option>
          <option value="Mr">MR</option>
          <option value="Mrs">MRS</option>
          <option value="Ms">MS</option>
        </select>
        {errors.title && <p className={errorClass}>{errors.title}</p>}
      </div>

      {/* Gender */}
      <div>
        <label className={labelClass}>
          Gender <span className="text-red-600">*</span>
        </label>
        <select
          className={inputClass + (errors.gender ? " border-red-500" : "")}
          value={formData?.gender || ""}
          onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
        >
          <option value="">Select Gender</option>
          <option value="Female">Female</option>
          <option value="Male">Male</option>
          <option value="Other">Other</option>
        </select>
        {errors.gender && <p className={errorClass}>{errors.gender}</p>}
      </div>

      {/* Name */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>
            Family Name <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            className={
              inputClass + (errors.family_name ? " border-red-500" : "")
            }
            value={formData?.family_name || ""}
            onChange={(e) =>
              setFormData({ ...formData, family_name: e.target.value })
            }
            placeholder="Family Name"
          />
          {errors.family_name && (
            <p className={errorClass}>{errors.family_name}</p>
          )}
        </div>
        <div>
          <label className={labelClass}>
            First Name <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            className={
              inputClass + (errors.first_name ? " border-red-500" : "")
            }
            value={formData?.first_name || ""}
            onChange={(e) =>
              setFormData({ ...formData, first_name: e.target.value })
            }
            placeholder="First Name"
          />
          {errors.first_name && (
            <p className={errorClass}>{errors.first_name}</p>
          )}
        </div>
      </div>

      {/* DOB */}
      <div>
        <label className={labelClass}>
          Date of Birth <span className="text-red-600">*</span>
        </label>
        <input
          type="date"
          className={inputClass + (errors.dob ? " border-red-500" : "")}
          value={formData?.dob || ""}
          onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
        />
        {errors.dob && <p className={errorClass}>{errors.dob}</p>}
      </div>

      {/* Address */}
      <div>
        <label className={labelClass}>
          Address <span className="text-red-600">*</span>
        </label>
        <input
          type="text"
          className={inputClass + (errors.address ? " border-red-500" : "")}
          value={formData?.address || ""}
          onChange={(e) =>
            setFormData({ ...formData, address: e.target.value })
          }
          placeholder="Address"
        />
        {errors.address && <p className={errorClass}>{errors.address}</p>}
      </div>

      {/* State, Suburb, Postcode */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className={labelClass}>
            State <span className="text-red-600">*</span>
          </label>
          <select
            className={inputClass + (errors.state ? " border-red-500" : "")}
            value={formData.state || ""}
            onChange={handleStateChange}
          >
            <option value="">Select State</option>

            {states.map((state) => (
              <option key={state.state_code} value={state.state_code}>
                {state.state}
              </option>
            ))}
          </select>
          {errors.state && <p className={errorClass}>{errors.state}</p>}
        </div>

        <div>
          <label className={labelClass}>
            Suburb <span className="text-red-600">*</span>
          </label>

          <div className="relative">
            <input
              type="text"
              className={
                inputClass +
                (errors.suburb ? " border-red-500 pr-10" : " pr-10") // add right padding for spinner space
              }
              value={suburbInput}
              onChange={handleSuburbInputChange}
              placeholder={
                formData.state
                  ? "Search suburb"
                  : "Select state first"
              }
              disabled={!formData.state}
              autoComplete="off"
            />

            {/* Spinner inside input */}
            {suburbLoading && (
              <div className="absolute inset-y-0 right-3 flex items-center">
                <svg
                  className="animate-spin h-4 w-4 text-gray-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
              </div>
            )}

            {/* Dropdown suggestions */}
            {!suburbLoading && suburbOptions.length > 0 && (
              <ul className="absolute left-0 top-full w-full border rounded bg-white shadow max-h-40 overflow-auto z-10">
                {suburbOptions.map((item) => (
                  <li
                    key={item.id}
                    className="px-3 py-1 hover:bg-blue-50 cursor-pointer"
                    onClick={() =>
                      handleSuburbSelect(item.suburb, item.postal_code)
                    }
                  >
                    {item.suburb} ({item.postal_code})
                  </li>
                ))}
              </ul>
            )}
          </div>

          {errors.suburb && <p className={errorClass}>{errors.suburb}</p>}
        </div>

        <div>
          <label className={labelClass}>
            Postcode <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            className={inputClass + (errors.postcode ? " border-red-500" : "")}
            placeholder="Postcode"
            value={formData.postcode || ""}
            onChange={(e) =>
              setFormData({ ...formData, postcode: e.target.value })
            }
          />
          {errors.postcode && <p className={errorClass}>{errors.postcode}</p>}
        </div>
      </div>

      {/* Contact */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>
            Mobile Number <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            className={
              inputClass + (errors.mobile_number ? " border-red-500" : "")
            }
            placeholder="Mobile Number"
            value={formData?.mobile_number || ""}
            onChange={(e) =>
              setFormData({ ...formData, mobile_number: e.target.value })
            }
          />
          {errors.mobile_number && (
            <p className={errorClass}>{errors.mobile_number}</p>
          )}
        </div>
      </div>

      {/* Transport */}
      <div>
        <label className={labelClass}>
          Transport <span className="text-red-600">*</span>
        </label>
        <div className="flex items-center space-x-8 mt-2">
          {/* Car Switch */}
          <label className="flex items-center cursor-pointer space-x-2">
            <input
              type="radio"
              name="transport"
              value="Car"
              checked={formData.transport === "Car"}
              onChange={() => setFormData({ ...formData, transport: "Car" })}
              className="sr-only peer"
            />
            <div
              className={`w-10 h-6 rounded-full transition-colors duration-200 
        ${formData.transport === "Car" ? "bg-blue-500" : "bg-gray-300"}`}
            >
              <div
                className={`w-5 h-5 bg-white rounded-full shadow 
          transform transition-transform duration-200
          ${formData.transport === "Car" ? "translate-x-5" : "translate-x-0"}`}
              ></div>
            </div>
            <span
              className={
                formData.transport === "Car"
                  ? "font-semibold text-blue-600"
                  : "text-gray-600"
              }
            >
              Car
            </span>
          </label>

          {/* Public Transport Switch */}
          <label className="flex items-center cursor-pointer space-x-2">
            <input
              type="radio"
              name="transport"
              value="Public_Transport"
              checked={formData.transport === "Public_Transport"}
              onChange={() =>
                setFormData({ ...formData, transport: "Public_Transport" })
              }
              className="sr-only peer"
            />
            <div
              className={`w-10 h-6 rounded-full transition-colors duration-200 
        ${
          formData.transport === "Public_Transport"
            ? "bg-blue-500"
            : "bg-gray-300"
        }`}
            >
              <div
                className={`w-5 h-5 bg-white rounded-full shadow 
          transform transition-transform duration-200
          ${
            formData.transport === "Public_Transport"
              ? "translate-x-5"
              : "translate-x-0"
          }`}
              ></div>
            </div>
            <span
              className={
                formData.transport === "Public_Transport"
                  ? "font-semibold text-blue-600"
                  : "text-gray-600"
              }
            >
              Public Transport
            </span>
          </label>
        </div>
        {errors.transport && (
          <p className="text-red-600 text-sm">{errors.transport}</p>
        )}
      </div>

      {/* Referral Source */}
      <div>
        <label className={labelClass}>
          How did you hear about us? <span className="text-red-600">*</span>
        </label>
        <select
          className={
            inputClass + (errors.referral_source ? " border-red-500" : "")
          }
          value={formData?.referral_source || ""}
          onChange={(e) =>
            setFormData({ ...formData, referral_source: e.target.value })
          }
        >
          <option value="">Select</option>
          <option value="Facebook">Facebook</option>
          <option value="Google Search">Google Search</option>
          <option value="Client Referral">Referral From Client</option>
          <option value="Employee Referral">Referral From Employee</option>
          <option value="Seek Search">Seek Search</option>
          <option value="Others">Others</option>
        </select>
        {errors.referral_source && (
          <p className={errorClass}>{errors.referral_source}</p>
        )}
      </div>

      {/* Location */}
      <div>
        <label className={labelClass}>
          Location applying for? <span className="text-red-600">*</span>
        </label>
        <select
          className={inputClass + (errors.location ? " border-red-500" : "")}
          value={formData?.location || ""}
          onChange={(e) =>
            setFormData({ ...formData, location: e.target.value })
          }
        >
          <option value="">Select Location</option>
          {states.map((state) => (
            <option key={state.state_code} value={state.state_code}>
              {state.state}
            </option>
          ))}
        </select>
        {errors.location && <p className={errorClass}>{errors.location}</p>}
      </div>

      {/* Expertise */}
      <div>
        <label className={labelClass}>Expertise</label>
        <select
          className={inputClass}
          value={formData?.expertise || ""}
          onChange={(e) =>
            setFormData({ ...formData, expertise: e.target.value })
          }
        >
          <option value="">Select Expertise</option>
          <option value="Aged Care">AGED CARE</option>
          <option value="Community Nursing">COMMUNITY NURSING</option>
          <option value="Specialized Nursing">SPECIALIZED NURSING</option>
          <option value="Disability Support">DISABILITY SUPPORT</option>
          <option value="Rural and Remote">RURAL AND REMOTE</option>
        </select>
      </div>

      {/* Experience */}
      <div>
        <label className={labelClass}>
          Experience <span className="text-red-600">*</span>
        </label>
        <select
          className={inputClass + (errors.experience ? " border-red-500" : "")}
          value={formData?.experience || ""}
          onChange={(e) =>
            setFormData({ ...formData, experience: e.target.value })
          }
        >
          <option value="">Select Experience</option>
          <option value="0 to 6 Months">0 to 6 Months</option>
          <option value="6 to 12 Months">6 to 12 Months</option>
          <option value="1 Year">1 Year</option>
          <option value="2 Years">2 Years</option>
          <option value="3 Years">3 Years</option>
          <option value="4 Years">4 Years</option>
          <option value="5 Years">5 Years Plus</option>
        </select>
        {errors.experience && <p className={errorClass}>{errors.experience}</p>}
      </div>

      {/* Availability */}
      <div>
        <label className={labelClass}>Availability</label>
        <select
          className={inputClass}
          value={formData?.availability || ""}
          onChange={(e) =>
            setFormData({ ...formData, availability: e.target.value })
          }
        >
          <option value="">Select Availability</option>
          <option value="Shifts Monday To Friday">
            Shifts Monday to Friday
          </option>
          <option value="Shifts Saturday To Sunday">
            Shifts Saturday to Sunday
          </option>
          <option value="One To Two Shifts Per Week">
            One To Two Shifts Per Week
          </option>
          <option value="Three To Four Shifts Per Week">
            Three To Four Shifts Per Week
          </option>
          <option value="Others">Others</option>
        </select>
      </div>

      {/* Buttons */}
      <div className={buttonBarClass}>
        <button className={backButtonClass} onClick={onBack} type="button">
          Back
        </button>
        <button
          className={submitButtonClass}
          onClick={handleSubmit}
          disabled={submitting || Object.keys(errors).length > 0}
          type="button"
        >
          {submitting ? "Submitting..." : "Submit Application"}
        </button>
      </div>
    </div>
  );
};

export default Step3PersonalDetails;
